import type { AxiosResponse } from 'axios';
import Store from 'Services/Store';

interface ExportItem {
    id: string;
    status: string;
    progress: number;
    public_url: string | null;
    started_at: string | null;
    created_at: string;
    listing_name: string;
    custom_name: string | null;
    owner_id: string;
    total_rows: number | null;
}

interface ExportOwner {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    status: string;
    avatar: string | null;
}

interface ExportPagination {
    offset: number;
    limit: number;
    total: number;
}

interface ExportSearchResponse {
    data: ExportItem[];
    pagination: ExportPagination;
}

interface ExportMetadataResponse {
    owners: ExportOwner[];
}

interface SearchParams {
    offset: number;
    limit: number;
    order?: string;
    direction?: string;
}

interface ExportListResponse {
    data: ExportItem[];
}

const getExports = async (
    params: Record<string, unknown>,
): Promise<AxiosResponse<ExportListResponse>> => {
    const currentStaffId = (Store.get('user') as { id: string }).id;
    return await ApiClient.post<ExportListResponse>('/listings/exports/search', params, {
        filters: { owner_id: currentStaffId, export_api: false },
    });
};

const searchExports = async (
    params: SearchParams,
): Promise<AxiosResponse<ExportSearchResponse>> => {
    return await ApiClient.post<ExportSearchResponse>('/listings/exports/search', params, {
        filters: { export_api: false },
    });
};

const getExport = async (id: string): Promise<AxiosResponse<ExportItem>> => {
    return await ApiClient.get<ExportItem>('/listings/exports/' + id);
};

const getMetadata = async (): Promise<AxiosResponse<ExportMetadataResponse>> => {
    return await ApiClient.get<ExportMetadataResponse>('/listings/exports/metadata');
};

const rescheduleExport = async (id: string): Promise<AxiosResponse> => {
    return await ApiClient.post(`/listings/exports/${id}/reschedule`);
};

const updateExportName = async (id: string, name: string): Promise<AxiosResponse<ExportItem>> => {
    return await ApiClient.put<ExportItem>(`/listings/exports/${id}`, {}, { custom_name: name });
};

const exportList = async (): Promise<AxiosResponse> => {
    return await ApiClient.post(
        '/listings/exports/export',
        {},
        {
            output_format: 'csv',
            charset: 'UTF-8',
            columns: null,
        },
    );
};

export default { getExports, getExport };
export {
    searchExports,
    getExports,
    getExport,
    getMetadata,
    rescheduleExport,
    updateExportName,
    exportList,
};
export type {
    ExportItem,
    ExportOwner,
    ExportPagination,
    ExportSearchResponse,
    ExportMetadataResponse,
    SearchParams,
};
